import { I18n } from "@assets/strings";
import React, { JSX } from "react";
import { Link } from "react-router";
import styled, { css } from "styled-components";
import { config } from "@core/config/config";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useIsLearner } from "@domain/currentProfile/hooks";
import { usePostCreation } from "@domain/post/hooks";
import { mobile } from "@ui/styles/utils";
import { Primary, PrimaryMedium } from "@ui/components/containers/primary";
import { CommunityIcon, EditIcon, HatIcon, KeyBoardIcon, QuizIcon, ShareroomIcon } from "@ui/components/icons";
import { row, Stack } from "@ui/components/layout";

type CreationMenuContentProps = React.HTMLAttributes<HTMLDivElement>;
export const CreationMenuContent: React.FC<CreationMenuContentProps> = ({ ...htmlProps }) => {
	const createPost = usePostCreation();
	const { trackingService } = useServices();
	const isLearner = useIsLearner();

	return (
		<Container {...htmlProps}>
			<Title>{I18n.t("navigation.create.title")}</Title>
			<CreationItem
				onClick={() => createPost()}
				icon={<EditIcon />}
				title={I18n.t("navigation.create.post.title")}
				description={I18n.t("navigation.create.post.description")}
			/>
			{isLearner ? null : (
				<>
					<Link to={Path.QuizEditor("new")}>
						<CreationItem
							icon={<QuizIcon />}
							title={I18n.t("navigation.create.quiz.title")}
							description={I18n.t("navigation.create.quiz.description")}
						/>
					</Link>
					<Link to={Path.ArticleEditor("new")}>
						<CreationItem
							icon={<KeyBoardIcon />}
							title={I18n.t("navigation.create.article.title")}
							description={I18n.t("navigation.create.article.description")}
						/>
					</Link>
					<Link to={Path.Lms.Editor("new")}>
						<CreationItem
							onClick={() => trackingService.track("course / start creating")}
							icon={<HatIcon />}
							title={I18n.t("navigation.create.course.title")}
							description={I18n.t("navigation.create.course.description")}
						/>
					</Link>
					<Link to={Path.GroupEditor("new", "DISCUSSION_GROUP")}>
						<CreationItem
							onClick={() => trackingService.track("Navbar - create - create a discussion group")}
							icon={<CommunityIcon />}
							title={I18n.t("navigation.create.discussion.title")}
							description={I18n.t("navigation.create.discussion.description")}
						/>
					</Link>
					{config.features.shareRooms ? (
						<Link to={Path.GroupEditor("new", "SHAREROOM")}>
							<CreationItem
								onClick={() => trackingService.track("Navbar - create - create classroom")}
								icon={<ShareroomIcon />}
								title={I18n.t("navigation.create.shareroom.title")}
								description={I18n.t("navigation.create.shareroom.description")}
							/>
						</Link>
					) : (
						<div style={{ pointerEvents: "none" }}>
							<CreationItem
								teasing
								icon={<ShareroomIcon />}
								title={I18n.t("navigation.create.shareroom.title")}
								description={I18n.t("navigation.create.shareroom.soon")}
							/>
						</div>
					)}
				</>
			)}
		</Container>
	);
};

const Container = styled.div`
	padding: 30px 0;
	
	@media ${mobile} {
		padding: 0;
		&:last-child {
			padding-bottom: 50px;
		}
	}
	
`;

const Title = styled(PrimaryMedium)`
	margin-left: 32px;
	@media ${mobile} {
		margin-left: 10px;
	}
	margin-bottom: 20px;
`;

interface CreationItemProps extends React.HTMLAttributes<HTMLDivElement> {
	icon: JSX.Element;
	title: string;
	description: string;
	teasing?: boolean;
}
const CreationItem: React.FC<CreationItemProps> = ({ icon, title, description, teasing, ...htmlProps }) => {
	return (
		<ItemContainer {...htmlProps}>
			<IconWrapper>{icon}</IconWrapper>
			<Stack gap={5}>
				<PrimaryMedium>{title}</PrimaryMedium>
				<Description teasing={teasing}>{description}</Description>
			</Stack>
		</ItemContainer>
	);
};

const ItemContainer = styled.div`
	cursor: pointer;
	border-radius: 9px;
	padding: 16px 32px;
	@media ${mobile} {
		padding: 10px;
	}
	${row(20)};

	&:hover {
		background-color: ${({ theme }) => theme.colors.bodyBackground};
	}
`;

const IconWrapper = styled.div`
	flex-shrink: 0;
	border-radius: 9px;
	width: 34px;
	height: 34px;
	background-color: ${({ theme }) => theme.colors.secondaryLight};
	${row(0, "center", "center")};

	> svg path {
		fill: ${({ theme }) => theme.colors.secondary};
	}
`;

const Description = styled(Primary)<{ teasing?: boolean }>`
	font-size: 0.8125rem;
	width: 190px;
	white-space: initial;
	${({ teasing }) =>
		teasing &&
		css`
			font-weight: bold;
			color: ${({ theme }) => theme.colors.secondary};
		`}
`;
